import { useEffect, useState } from "react";
import { Plus, X, User, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PrimaryButton } from "@/components/dash/Ui";

const inputCls = "bg-slate-950 border-slate-800 focus-visible:ring-emerald-500";
const origin = window.location.host;

const ModeTab = ({ active, onClick, icon: Icon, label, id }) => (
  <button type="button" data-testid={`link-mode-${id}`} onClick={onClick}
    className={`flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-colors ${
      active ? "bg-emerald-500 text-slate-950" : "text-slate-400 hover:text-white"}`}>
    <Icon className="w-4 h-4" /> {label}
  </button>
);

export const LinkDialog = ({ value, onClose, onSave }) => {
  const [form, setForm] = useState(value);
  useEffect(() => setForm(value), [value]);
  if (!form) return null;
  const multi = form.mode === "multi";
  const nums = form.numbers;
  const setNum = (i, key, v) => setForm({ ...form, numbers: nums.map((n, j) => (j === i ? { ...n, [key]: v } : n)) });
  const shown = multi ? nums : nums.slice(0, 1);

  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-h-[90vh] overflow-y-auto" data-testid="link-dialog">
        <DialogHeader><DialogTitle className="font-display">{form.id ? "Edit link" : "Generate link"}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, numbers: shown }); }} className="space-y-4">
          <div className="flex gap-1 rounded-2xl border border-slate-800 bg-slate-950 p-1">
            <ModeTab id="single" icon={User} label="Single number" active={!multi} onClick={() => setForm({ ...form, mode: "single" })} />
            <ModeTab id="multi" icon={Users} label="Multi-agent" active={multi} onClick={() => setForm({ ...form, mode: "multi" })} />
          </div>
          <label className="block text-sm text-slate-400">Your link
            <div className="flex items-center mt-2 rounded-md border border-slate-800 bg-slate-950 pl-3">
              <span className="font-mono text-sm text-slate-500 whitespace-nowrap">{origin}/</span>
              <Input data-testid="link-slug-input" required value={form.slug} placeholder="daddyhere"
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                className="border-0 bg-transparent font-mono focus-visible:ring-0 pl-1" />
            </div>
          </label>
          <label className="block text-sm text-slate-400">Title
            <Input data-testid="link-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`${inputCls} mt-2`} />
          </label>
          <div>
            <p className="text-sm text-slate-400 mb-2">{multi ? "WhatsApp numbers (a random one is used per click)" : "WhatsApp number (with country code)"}</p>
            <div className="space-y-2">
              {shown.map((n, i) => (
                <div key={i} className="flex gap-2">
                  {multi && <Input data-testid={`link-number-name-${i}`} placeholder="Agent name" value={n.name}
                    onChange={(e) => setNum(i, "name", e.target.value)} className={`${inputCls} w-36`} />}
                  <Input data-testid={`link-number-phone-${i}`} required placeholder="919876543210" value={n.phone}
                    onChange={(e) => setNum(i, "phone", e.target.value)} className={`${inputCls} font-mono flex-1`} />
                  {multi && nums.length > 1 && (
                    <button type="button" data-testid={`link-number-remove-${i}`} onClick={() => setForm({ ...form, numbers: nums.filter((_, j) => j !== i) })}
                      className="p-2 text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
            </div>
            {multi && (
              <button type="button" data-testid="link-number-add" onClick={() => setForm({ ...form, numbers: [...nums, { name: "", phone: "" }] })}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300">
                <Plus className="w-4 h-4" /> Add number
              </button>
            )}
          </div>
          <label className="block text-sm text-slate-400">Pre-filled message
            <Textarea data-testid="link-message-input" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} mt-2`} />
          </label>
          <PrimaryButton data-testid="link-save-button" type="submit" className="w-full justify-center">{form.id ? "Update link" : "Generate link"}</PrimaryButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};
